import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "list", "selected", "selectedName", "confirmBtn", "confirmText", "recipeBtn", "toggleAllBtn", "selectionHint"]
  static values = { ingredientId: Number, selectedId: Number }

  connect() {
    document.body.style.overflow = "hidden"
    this.searchTarget.focus()
    this.selectedRecipes = new Set()
    this.allRecipeIds = this.recipeBtnTargets.map(btn => btn.dataset.recipeId)
    this.updateConfirmState()
  }

  disconnect() {
    document.body.style.overflow = ""
  }

  toggleRecipe(event) {
    const btn = event.currentTarget
    const recipeId = btn.dataset.recipeId

    if (this.selectedRecipes.has(recipeId)) {
      this.selectedRecipes.delete(recipeId)
      btn.classList.remove("border-electric", "bg-electric/10", "text-electric")
      btn.classList.add("border-cream/10", "text-cream/60")
      btn.querySelector("svg").classList.add("opacity-0")
      btn.querySelector(".recipe-check").classList.remove("border-electric", "bg-electric/20")
      btn.querySelector(".recipe-check").classList.add("border-cream/20")
    } else {
      this.selectedRecipes.add(recipeId)
      btn.classList.add("border-electric", "bg-electric/10", "text-electric")
      btn.classList.remove("border-cream/10", "text-cream/60")
      btn.querySelector("svg").classList.remove("opacity-0")
      btn.querySelector(".recipe-check").classList.add("border-electric", "bg-electric/20")
      btn.querySelector(".recipe-check").classList.remove("border-cream/20")
    }

    this.updateToggleAllText()
    this.updateConfirmState()
  }

  toggleAll() {
    const allSelected = this.selectedRecipes.size === this.allRecipeIds.length

    if (allSelected) {
      this.selectedRecipes.clear()
      this.recipeBtnTargets.forEach(btn => {
        btn.classList.remove("border-electric", "bg-electric/10", "text-electric")
        btn.classList.add("border-cream/10", "text-cream/60")
        btn.querySelector("svg").classList.add("opacity-0")
        btn.querySelector(".recipe-check").classList.remove("border-electric", "bg-electric/20")
        btn.querySelector(".recipe-check").classList.add("border-cream/20")
      })
    } else {
      this.allRecipeIds.forEach(id => this.selectedRecipes.add(id))
      this.recipeBtnTargets.forEach(btn => {
        btn.classList.add("border-electric", "bg-electric/10", "text-electric")
        btn.classList.remove("border-cream/10", "text-cream/60")
        btn.querySelector("svg").classList.remove("opacity-0")
        btn.querySelector(".recipe-check").classList.add("border-electric", "bg-electric/20")
        btn.querySelector(".recipe-check").classList.remove("border-cream/20")
      })
    }

    this.updateToggleAllText()
    this.updateConfirmState()
  }

  updateToggleAllText() {
    const allSelected = this.selectedRecipes.size === this.allRecipeIds.length
    this.toggleAllBtnTarget.textContent = allSelected ? "Deseleccionar todas" : "Seleccionar todas"
  }

  updateConfirmState() {
    const hasSelection = this.selectedRecipes.size > 0
    const hasReplacement = this.selectedIdValue

    this.confirmBtnTarget.disabled = !hasSelection || !hasReplacement

    if (hasSelection) {
      const count = this.selectedRecipes.size
      const isAll = count === this.allRecipeIds.length
      if (isAll && this.allRecipeIds.length === 1) {
        this.confirmTextTarget.textContent = "Reemplazar y eliminar"
      } else if (isAll) {
        this.confirmTextTarget.textContent = "Reemplazar en todas y eliminar"
      } else {
        this.confirmTextTarget.textContent = `Reemplazar en ${count} receta${count > 1 ? 's' : ''}`
      }
    } else {
      this.confirmTextTarget.textContent = "Reemplazar"
    }

    if (this.hasSelectionHintTarget) {
      this.selectionHintTarget.classList.toggle("hidden", hasSelection)
    }
  }

  filter() {
    const query = this.normalize(this.searchTarget.value.toLowerCase().trim())
    const options = this.listTarget.querySelectorAll(".ingredient-option")

    options.forEach(option => {
      const name = this.normalize(option.dataset.ingredientName.toLowerCase())
      const category = option.dataset.ingredientCategory ? this.normalize(option.dataset.ingredientCategory.toLowerCase()) : ""
      const matches = query === "" || name.includes(query) || category.includes(query)
      option.style.display = matches ? "" : "none"
    })
  }

  normalize(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  }

  showList() {
    this.listTarget.classList.remove("hidden")
  }

  hideList() {
    setTimeout(() => {
      this.listTarget.classList.add("hidden")
    }, 150)
  }

  select(event) {
    const btn = event.currentTarget
    this.selectedIdValue = parseInt(btn.dataset.ingredientId)
    this.selectedNameTarget.textContent = btn.dataset.ingredientName
    this.selectedTarget.classList.remove("hidden")
    this.searchTarget.value = ""
    this.listTarget.classList.add("hidden")
    this.updateConfirmState()
  }

  clearSelection() {
    this.selectedIdValue = null
    this.selectedTarget.classList.add("hidden")
    this.searchTarget.focus()
    this.updateConfirmState()
  }

  async confirm() {
    if (!this.selectedIdValue || this.selectedRecipes.size === 0) return

    this.confirmBtnTarget.disabled = true
    this.confirmTextTarget.textContent = "Procesando..."

    const csrfToken = document.querySelector("meta[name='csrf-token']").content
    const recipeIds = Array.from(this.selectedRecipes).join(",")

    const response = await fetch(`/ingredients/${this.ingredientIdValue}/replace_and_destroy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: `replacement_id=${this.selectedIdValue}&recipe_ids=${recipeIds}`
    })

    if (response.ok) {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
    }
  }

  close() {
    const container = document.getElementById("modal-container")
    if (container) {
      container.innerHTML = ""
    }
    document.body.style.overflow = ""
  }

  stop(event) {
    event.stopPropagation()
  }
}
